import { accessReviewRoutes } from "./access-review.js";
import { appAdoptionsRoutes } from "./app-adoptions.js";
import { appsCatalogRoutes } from "./apps-catalog.js";
import { appsRoutes } from "./apps.js";
import { auditLogRoutes } from "./audit-log.js";
import { authProvidersRoutes } from "./auth-providers.js";
import { authRoutes } from "./auth.js";
import { blobBucketsRoutes } from "./blob-buckets.js";
import { channelOffersRoutes } from "./channel-offers.js";
import { channelsRoutes } from "./channels.js";
import { dcrRedirectHostsRoutes } from "./dcr-redirect-hosts.js";
import { envelopeErrorsRoutes } from "./envelope-errors.js";
import { usersRoutes } from "./users.js";
import { groupsRoutes } from "./groups.js";
import { isRequestPath } from "./request-path.js";
import { invitationsRoutes } from "./invitations.js";
import { llmCredentialsRoutes } from "./llm-credentials.js";
import { minimumRingRoutes } from "./minimum-ring.js";
import { notificationsRoutes } from "./notifications.js";
import { oauthClientsRoutes } from "./oauth-clients.js";
import { rootShellRoutes } from "./root-shell.js";
import { orgRoutes } from "./org.js";
import { permissionsRoutes } from "./permissions.js";
import { platformRoutes } from "./platform.js";
import { recoveryCodesRoutes } from "./recovery-codes.js";
import { roleAppPermissionsRoutes } from "./role-app-permissions.js";
import { rolesRoutes } from "./roles.js";
import { scimTokensRoutes } from "./scim-tokens.js";
import { sessionsRoutes } from "./sessions.js";
import { machineTrustPoliciesRoutes } from "./machine-trust-policies.js";
import { machinePrincipalsRoutes } from "./machine-principals.js";
import { principalsRoutes } from "./principals.js";
import { kernelConsentsRoutes } from "./kernel-consents.js";
import { userPasskeysRoutes } from "./user-passkeys.js";
import { stepUpRoutes } from "./step-up-routes.js";
import { secondFactorPolicyRoutes } from "./second-factor-policy.js";
import { z } from "zod";
import { KERNEL_TIERS, kernelRightSchema } from "@zion/sdk-contracts";
//#region src/route-census.ts
/**
* Every route this package names, folded from the family files, for core-api's
* `admin-route-census.spec.ts` (`00-index.md` §4 item 5, `security-audit.md` chain B step 3).
* The spec asserts each `(method, path)` is a key in `openapi.json`, so a route the app calls
* and core-api stops serving fails `pnpm --filter @zion/core-api test`.
*
* Also exported on the subpath `@zion/sdk-dp-admin-contracts/route-census`. The fold imports every
* family module, so the subpath saves a consumer nothing at load; it exists so the census reads one
* named module.
*
* Kernel apps (WS-2b, `design-docs/WS-2.md` section 3.3) add three things here. The kernel fold
* `KERNEL_CONTRACT_ROUTES` holds the ten new routes apart from the 130 of the admin fold (eight of
* WS-2b, and the two second-factor policy routes of WS-34, R-154).
* `CONTRACT_ROUTE_KERNEL_CLASS` gives every route of both folds its kernel class, and
* `kernelClassOf` reads it. `matchContractRoute` maps a request path to its entry.
*/
/**
* One entry per family, in the order of `00-index.md` §1.1, so a census diff reads by family.
* `sessions` and `auth` are appended after `apps` (zion-admin-app `uncovered-admin-routes`),
* `machine-trust-policies` and `machine-principals` after them (2026-10-01-dp-oidc-federation
* WS-3), then `root-shell` (2026-09-25-layered-shell-surfaces R-60), so every earlier family keeps
* its position in the fold. `principals` follows root-shell (admin-redesign-asks C-4, #3682),
* `app-adoptions` follows principals (2026-10-06-tenant-app-adoption C-2), and `blob-buckets`
* follows app-adoptions (2026-10-13-blob-buckets R-071 item 6). `access-review` follows
* blob-buckets (2026-10-22-dp-admin-insights, S1 contracts item 1).
*/
const ADMIN_CONTRACT_ROUTE_FAMILIES = Object.freeze({
	users: usersRoutes,
	groups: groupsRoutes,
	roles: rolesRoutes,
	invitations: invitationsRoutes,
	org: orgRoutes,
	"auth-providers": authProvidersRoutes,
	"scim-tokens": scimTokensRoutes,
	"oauth-clients": oauthClientsRoutes,
	"dcr-redirect-hosts": dcrRedirectHostsRoutes,
	"llm-credentials": llmCredentialsRoutes,
	"apps-catalog": appsCatalogRoutes,
	notifications: notificationsRoutes,
	"audit-log": auditLogRoutes,
	"envelope-errors": envelopeErrorsRoutes,
	permissions: permissionsRoutes,
	"recovery-codes": recoveryCodesRoutes,
	"role-app-permissions": roleAppPermissionsRoutes,
	"minimum-ring": minimumRingRoutes,
	channels: channelsRoutes,
	"channel-offers": channelOffersRoutes,
	platform: platformRoutes,
	apps: appsRoutes,
	sessions: sessionsRoutes,
	auth: authRoutes,
	"machine-trust-policies": machineTrustPoliciesRoutes,
	"machine-principals": machinePrincipalsRoutes,
	"root-shell": rootShellRoutes,
	principals: principalsRoutes,
	"app-adoptions": appAdoptionsRoutes,
	"blob-buckets": blobBucketsRoutes,
	"access-review": accessReviewRoutes
});
/**
* Every route, flattened. 130 entries: 107 under `/v1/admin` and 23 outside it. The entries are
* the routes of WS-6 §7, with these changes:
*
* - plus the three `/v1/release/channel-offers` routes of 2026-09-24-private-release-channels R-027;
* - plus the twelve routes of zion-admin-app `uncovered-admin-routes`: ten under `/v1/admin`,
*   `POST /v1/release/channels/{id}/canary` and `GET /v1/auth/me`;
* - plus `POST /v1/admin/groups/{id}/child-groups` of
*   2026-09-30-agent-streams-and-app-owned-groups R-003;
* - plus the three trust-policy and seven machine-principal routes of 2026-10-01-dp-oidc-federation;
* - plus the remove-edge and move routes of 2026-10-02-directory-asks-platform, #3597, all under
*   `/v1/admin`;
* - plus the three `root-shell` routes of 2026-09-25-layered-shell-surfaces R-60: `GET` and
*   `PUT /v1/admin/root-shell` and `GET /v1/root-shell`;
* - plus the four `principals` routes of admin-redesign-asks C-4 under `/v1/admin/principals`;
* - plus the five `app-adoptions` routes of 2026-10-06-tenant-app-adoption C-2 under
*   `/v1/admin/app-adoptions`;
* - plus the two `blob-buckets` routes of 2026-10-13-blob-buckets R-071 item 6 under
*   `/v1/admin/blob-buckets`, and its relocation request route of R-089 item 2 under the same
*   prefix;
* - plus the app-adoptions locations read of R-119 item 4 under `/v1/admin/app-adoptions`;
* - less the three `/v1/platform/intro-animation` routes that kernel-apps R-181 removed;
* - plus the access-review people read of 2026-10-22-dp-admin-insights under
*   `/v1/admin/access-review`;
* - plus `GET /v1/release/channels/{id}/canaries` of 2026-10-23-canary-app-selector D-5.
*/
const ADMIN_CONTRACT_ROUTES = Object.freeze(Object.values(ADMIN_CONTRACT_ROUTE_FAMILIES).flat());
/** `METHOD path`, the key shape a census compares on. */
function routeKey(entry) {
	return `${entry.method} ${entry.path}`;
}
/**
* The strict parse of {@link KernelRouteClass}, discriminated on `kind`. `right` must be one of the
* 14 kernel rights of `kernelRightSchema`, and `tier` one of `KERNEL_TIERS` (0, 1, 2). The parse
* refuses an unknown right, a tier of 3, an unknown `kind` and any extra key. No `.transform()`.
*/
const kernelRouteClassSchema = z.discriminatedUnion("kind", [
	z.strictObject({
		kind: z.literal("kernel"),
		right: kernelRightSchema,
		tier: z.literal([...KERNEL_TIERS])
	}),
	z.strictObject({ kind: z.literal("kernel-session") }),
	z.strictObject({ kind: z.literal("plain") })
]);
/**
* The kernel fold: the ten routes of kernel apps, apart from {@link ADMIN_CONTRACT_ROUTE_FAMILIES}.
* Four families in this order: `kernel-consents` (3 routes), `user-passkeys` (1), `step-up` (4)
* and `second-factor-policy` (2). The fold stays apart because core-api's
* `admin-route-census.spec.ts` pins the 130 routes of {@link ADMIN_CONTRACT_ROUTES}, and core-api
* serves these routes only from stage 3 (`design-docs/WS-2.md` open question 9). WS-34 appends
* `second-factor-policy` last (R-154), so every earlier family keeps its position in the fold. No
* key of this fold is a key of the admin fold.
*/
const KERNEL_CONTRACT_ROUTE_FAMILIES = Object.freeze({
	"kernel-consents": kernelConsentsRoutes,
	"user-passkeys": userPasskeysRoutes,
	"step-up": stepUpRoutes,
	"second-factor-policy": secondFactorPolicyRoutes
});
/** Every route of the kernel fold, flattened: 10 entries, frozen. */
const KERNEL_CONTRACT_ROUTES = Object.freeze(Object.values(KERNEL_CONTRACT_ROUTE_FAMILIES).flat());
/** One frozen `kernel` class. */
function kernel(right, tier) {
	return Object.freeze({
		kind: "kernel",
		right,
		tier
	});
}
const KERNEL_SESSION = Object.freeze({ kind: "kernel-session" });
const PLAIN = Object.freeze({ kind: "plain" });
/**
* One frozen class per {@link routeKey} of both folds: 140 keys, 130 of the admin fold and 10 of the
* kernel fold. The totals: 42 `kernel` tier 0, 53 tier 1, 31 tier 2, 4 `kernel-session`, 10 `plain`.
*
* The base rule (locked decision 12, default I-4): every `/v1/admin/` route is `kernel`, and every
* read is tier 0. `research/p10-route-catalog.md` section 1 gives each write's tier. Rulings and
* open-question defaults of `design-docs/WS-2.md` that shape single rows:
*
* - `GET /v1/admin/audit-log` and `GET /v1/admin/sessions` are `audit.read` tier 0 (open question 4).
* - `GET /v1/admin/access-review/people` is `audit.read` tier 0, the class of the audit log: the
*   page serves an auditor (2026-10-22-dp-admin-insights D-15).
* - The 13 channel and offer writes outside `/v1/admin/` are `tenant.settings` tier 2 (open question 5).
* - `POST /v1/admin/notifications/apps/ack` is tier 0: it marks notices read (open question 6).
* - The consent routes are `authority.grants`, the list at tier 0 (open questions 7 and 8).
* - `DELETE /v1/admin/users/{id}/passkeys` is `identity.users` tier 1 (R-054).
* - `GET /v1/admin/second-factor-policy` is `identity.providers` tier 0 and the `PUT` is tier 1
*   (R-054, R-154): the classes the two `NON_CONTRACT_KERNEL_ROUTES` rows of core-api held before R-154 moved them here.
* - `PATCH /v1/admin/users/{id}` is `identity.users` tier 1: its body can set `status`, so it
*   deactivates a user the same as `DELETE /v1/admin/users/{id}` (R-142).
* - `POST /v1/admin/invitations` is `identity.users` tier 1: the invite fixes the role that the
*   invitee's account gets at accept, up to `owner`, so it is the same role grant as
*   `POST /v1/admin/users` and `PATCH /v1/admin/users/{id}/role`, which are tier 1 (WS-19b-R1).
*   The revoke `DELETE /v1/admin/invitations/{id}` only lowers authority and stays tier 2.
* - `PUT /v1/admin/groups/{id}/channel` is `tenant.settings` (open question 12).
* - `POST /v1/admin/groups/{id}/members` is `authority.groups` tier 1: a member inherits every org
*   role that the group binds, so the add is the same role grant as
*   `POST /v1/admin/roles/groups/{groupId}` and `POST /v1/admin/groups/{id}/child-groups`, which are
*   tier 1 (R-143 point 5). The removal `DELETE /v1/admin/groups/{id}/members/{userId}` only lowers
*   authority and stays tier 2.
* - The four `principals` routes of admin-redesign-asks C-4 are `machine.principals`, as their
*   `machine-principals` twins are: the two reads tier 0, the update and the revoke tier 1.
* - The five `app-adoptions` routes of 2026-10-06-tenant-app-adoption are `authority.grants` (D-11):
*   the list tier 0, accept and resume tier 1 (they give an app access to org data, as a kernel
*   consent does), pause and remove tier 2 (they only take access away). The locations read of
*   2026-10-13-blob-buckets R-119 item 4 takes the class of the list, tier 0.
* - The two `blob-buckets` routes of 2026-10-13-blob-buckets R-071 item 6 are `tenant.settings`, as
*   `GET` and `PATCH /v1/admin/org` are: the list tier 0, the update tier 2. The relocation request
*   of R-089 item 2 carries the mark of the update: `tenant.settings` tier 2.
*
* The 10 `plain` keys are the five channel reads, the offers list, `GET /v1/platform/status`,
* `GET /v1/apps/{appId}/scaling-events`, `GET /v1/auth/me` and `GET /v1/root-shell`.
* The map and each class are frozen; an assignment throws.
*/
const CONTRACT_ROUTE_KERNEL_CLASS = Object.freeze({
	"GET /v1/admin/users": kernel("identity.users", 0),
	"GET /v1/admin/users/{id}": kernel("identity.users", 0),
	"GET /v1/admin/users/{id}/role-app-visibility-preview": kernel("identity.users", 0),
	"PATCH /v1/admin/users/{id}/role": kernel("identity.users", 1),
	"DELETE /v1/admin/users/{id}": kernel("identity.users", 1),
	"POST /v1/admin/users/{id}/reactivate": kernel("identity.users", 1),
	"PATCH /v1/admin/users/{id}": kernel("identity.users", 1),
	"DELETE /v1/admin/users/{id}/membership": kernel("identity.users", 1),
	"POST /v1/admin/users/{id}/transfer-ownership": kernel("identity.users", 1),
	"DELETE /v1/admin/users/{id}/sessions": kernel("identity.users", 2),
	"GET /v1/admin/groups": kernel("authority.groups", 0),
	"GET /v1/admin/groups/{id}": kernel("authority.groups", 0),
	"POST /v1/admin/groups": kernel("authority.groups", 1),
	"DELETE /v1/admin/groups/{id}": kernel("authority.groups", 1),
	"PUT /v1/admin/groups/{id}/channel": kernel("tenant.settings", 2),
	"POST /v1/admin/groups/{id}/members": kernel("authority.groups", 1),
	"DELETE /v1/admin/groups/{id}/members/{userId}": kernel("authority.groups", 2),
	"PATCH /v1/admin/groups/{id}": kernel("authority.groups", 1),
	"POST /v1/admin/groups/{id}/child-groups": kernel("authority.groups", 1),
	"DELETE /v1/admin/groups/{id}/child-groups/{childId}": kernel("authority.groups", 2),
	"POST /v1/admin/groups/{id}/child-groups/{childId}/move": kernel("authority.groups", 1),
	"GET /v1/admin/roles": kernel("identity.roles", 0),
	"GET /v1/admin/roles/{slug}": kernel("identity.roles", 0),
	"POST /v1/admin/roles": kernel("identity.roles", 1),
	"PUT /v1/admin/roles/{slug}": kernel("identity.roles", 1),
	"DELETE /v1/admin/roles/{slug}": kernel("identity.roles", 1),
	"POST /v1/admin/roles/members/{userId}": kernel("identity.roles", 1),
	"DELETE /v1/admin/roles/members/{userId}/{roleSlug}": kernel("identity.roles", 2),
	"POST /v1/admin/roles/groups/{groupId}": kernel("identity.roles", 1),
	"DELETE /v1/admin/roles/groups/{groupId}/{roleSlug}": kernel("identity.roles", 2),
	"POST /v1/admin/invitations": kernel("identity.users", 1),
	"GET /v1/admin/invitations": kernel("identity.users", 0),
	"DELETE /v1/admin/invitations/{id}": kernel("identity.users", 2),
	"GET /v1/admin/org": kernel("tenant.settings", 0),
	"PATCH /v1/admin/org": kernel("tenant.settings", 2),
	"POST /v1/admin/org/verify-domain": kernel("tenant.settings", 2),
	"GET /v1/admin/org/domain-status": kernel("tenant.settings", 0),
	"POST /v1/admin/org/archive": kernel("tenant.settings", 1),
	"GET /v1/admin/auth-providers/catalog": kernel("identity.providers", 0),
	"GET /v1/admin/auth-providers": kernel("identity.providers", 0),
	"POST /v1/admin/auth-providers": kernel("identity.providers", 1),
	"PATCH /v1/admin/auth-providers/{id}": kernel("identity.providers", 1),
	"DELETE /v1/admin/auth-providers/{id}": kernel("identity.providers", 1),
	"GET /v1/admin/scim-tokens": kernel("identity.providers", 0),
	"POST /v1/admin/scim-tokens": kernel("identity.providers", 1),
	"DELETE /v1/admin/scim-tokens/{id}": kernel("identity.providers", 2),
	"GET /v1/admin/oauth-clients": kernel("identity.providers", 0),
	"POST /v1/admin/oauth-clients": kernel("identity.providers", 1),
	"PATCH /v1/admin/oauth-clients/{id}": kernel("identity.providers", 1),
	"DELETE /v1/admin/oauth-clients/{id}": kernel("identity.providers", 1),
	"GET /v1/admin/dcr-redirect-hosts": kernel("identity.providers", 0),
	"POST /v1/admin/dcr-redirect-hosts": kernel("identity.providers", 1),
	"PATCH /v1/admin/dcr-redirect-hosts/{id}": kernel("identity.providers", 1),
	"DELETE /v1/admin/dcr-redirect-hosts/{id}": kernel("identity.providers", 1),
	"GET /v1/admin/llm-credentials": kernel("credentials.llm", 0),
	"PUT /v1/admin/llm-credentials/{provider}": kernel("credentials.llm", 1),
	"DELETE /v1/admin/llm-credentials/{provider}": kernel("credentials.llm", 1),
	"POST /v1/admin/llm-credentials/{provider}/test": kernel("credentials.llm", 2),
	"GET /v1/admin/apps-catalog": kernel("authority.grants", 0),
	"GET /v1/admin/apps-catalog/retained": kernel("authority.grants", 0),
	"GET /v1/admin/apps-catalog/{appId}": kernel("authority.grants", 0),
	"GET /v1/admin/apps-catalog/{appId}/history": kernel("authority.grants", 0),
	"POST /v1/admin/apps-catalog/{appId}/onboard-defaults": kernel("authority.grants", 1),
	"POST /v1/admin/apps-catalog/{appId}/grants": kernel("authority.grants", 1),
	"DELETE /v1/admin/apps-catalog/{appId}/grants/{kind}/{id}/{key}": kernel("authority.grants", 1),
	"GET /v1/admin/apps-catalog/{appId}/capabilities": kernel("authority.grants", 0),
	"PUT /v1/admin/apps-catalog/{appId}/capabilities": kernel("authority.grants", 1),
	"PUT /v1/admin/apps-catalog/{appId}/platform-permissions/{key}": kernel("authority.grants", 1),
	"DELETE /v1/admin/apps-catalog/{appId}/platform-permissions/{key}": kernel("authority.grants", 1),
	"GET /v1/admin/notifications/apps": kernel("tenant.settings", 0),
	"POST /v1/admin/notifications/apps/ack": kernel("tenant.settings", 0),
	"GET /v1/admin/audit-log": kernel("audit.read", 0),
	"GET /v1/admin/envelope-errors": kernel("tenant.settings", 0),
	"GET /v1/admin/permissions": kernel("identity.roles", 0),
	"GET /v1/admin/recovery-codes/count": kernel("identity.providers", 0),
	"POST /v1/admin/recovery-codes/regenerate": kernel("identity.providers", 1),
	"GET /v1/admin/role-app-permissions/{roleSlug}": kernel("identity.roles", 0),
	"POST /v1/admin/role-app-permissions": kernel("identity.roles", 1),
	"DELETE /v1/admin/role-app-permissions/{roleSlug}/{appId}/{permissionSlug}": kernel("identity.roles", 2),
	"GET /v1/admin/minimum-ring": kernel("tenant.settings", 0),
	"GET /v1/release/channels": PLAIN,
	"POST /v1/release/channels": kernel("tenant.settings", 2),
	"PATCH /v1/release/channels/{id}": kernel("tenant.settings", 2),
	"DELETE /v1/release/channels/{id}": kernel("tenant.settings", 2),
	"PUT /v1/release/channels/{id}/assignments": kernel("tenant.settings", 2),
	"POST /v1/release/channels/{id}/canary": kernel("tenant.settings", 2),
	"POST /v1/release/channels/{id}/canary/advance": kernel("tenant.settings", 2),
	"POST /v1/release/channels/{id}/canary/pause": kernel("tenant.settings", 2),
	"POST /v1/release/channels/{id}/canary/resume": kernel("tenant.settings", 2),
	"POST /v1/release/channels/{id}/canary/rollback": kernel("tenant.settings", 2),
	"GET /v1/release/channels/{id}/canary": PLAIN,
	"GET /v1/release/channels/{id}/canaries": PLAIN,
	"GET /v1/release/channels/{id}": PLAIN,
	"GET /v1/release/customer-channels/app-bindings": PLAIN,
	"PUT /v1/release/customer-channels/app-bindings/{appId}": kernel("tenant.settings", 2),
	"DELETE /v1/release/customer-channels/app-bindings/{appId}": kernel("tenant.settings", 2),
	"GET /v1/release/channel-offers": PLAIN,
	"POST /v1/release/channel-offers/{shareId}/accept": kernel("tenant.settings", 2),
	"POST /v1/release/channel-offers/{shareId}/decline": kernel("tenant.settings", 2),
	"GET /v1/platform/status": PLAIN,
	"GET /v1/apps/{appId}/scaling-events": PLAIN,
	"GET /v1/auth/me": PLAIN,
	"GET /v1/admin/sessions": kernel("audit.read", 0),
	"DELETE /v1/admin/sessions/{id}": kernel("identity.users", 2),
	"GET /v1/admin/machine-trust-policies": kernel("machine.principals", 0),
	"POST /v1/admin/machine-trust-policies": kernel("machine.principals", 1),
	"PATCH /v1/admin/machine-trust-policies/{id}": kernel("machine.principals", 1),
	"GET /v1/admin/machine-principals": kernel("machine.principals", 0),
	"POST /v1/admin/machine-principals": kernel("machine.principals", 1),
	"POST /v1/admin/machine-principals/{id}/revoke": kernel("machine.principals", 1),
	"GET /v1/admin/machine-principals/{id}/keys": kernel("machine.keys", 0),
	"POST /v1/admin/machine-principals/{id}/keys": kernel("machine.keys", 1),
	"DELETE /v1/admin/machine-principals/{id}/keys/{kid}": kernel("machine.keys", 1),
	"POST /v1/admin/machine-principals/{id}/enroll-tokens": kernel("machine.keys", 1),
	"GET /v1/root-shell": PLAIN,
	"GET /v1/admin/root-shell": kernel("tenant.settings", 0),
	"PUT /v1/admin/root-shell": kernel("tenant.settings", 2),
	"GET /v1/admin/principals": kernel("machine.principals", 0),
	"GET /v1/admin/principals/{id}": kernel("machine.principals", 0),
	"PATCH /v1/admin/principals/{id}": kernel("machine.principals", 1),
	"POST /v1/admin/principals/{id}/revoke": kernel("machine.principals", 1),
	"GET /v1/admin/app-adoptions": kernel("authority.grants", 0),
	"POST /v1/admin/app-adoptions/{appId}/accept": kernel("authority.grants", 1),
	"POST /v1/admin/app-adoptions/{appId}/pause": kernel("authority.grants", 2),
	"POST /v1/admin/app-adoptions/{appId}/resume": kernel("authority.grants", 1),
	"POST /v1/admin/app-adoptions/{appId}/remove": kernel("authority.grants", 2),
	"GET /v1/admin/app-adoptions/blob-bucket-locations": kernel("authority.grants", 0),
	"GET /v1/admin/blob-buckets": kernel("tenant.settings", 0),
	"PATCH /v1/admin/blob-buckets/{appSlug}/{logical}": kernel("tenant.settings", 2),
	"POST /v1/admin/blob-buckets/{appSlug}/{logical}/relocations": kernel("tenant.settings", 2),
	"GET /v1/admin/access-review/people": kernel("audit.read", 0),
	"GET /v1/admin/kernel-consents": kernel("authority.grants", 0),
	"POST /v1/admin/kernel-consents": kernel("authority.grants", 1),
	"DELETE /v1/admin/kernel-consents/{id}": kernel("authority.grants", 1),
	"DELETE /v1/admin/users/{id}/passkeys": kernel("identity.users", 1),
	"POST /v1/auth/step-up/requests": KERNEL_SESSION,
	"GET /v1/auth/step-up/requests/{id}": KERNEL_SESSION,
	"POST /v1/auth/step-up/requests/{id}/assertion": KERNEL_SESSION,
	"POST /v1/auth/step-up/requests/{id}/deny": KERNEL_SESSION,
	"GET /v1/admin/second-factor-policy": kernel("identity.providers", 0),
	"PUT /v1/admin/second-factor-policy": kernel("identity.providers", 1)
});
/**
* The kernel class of one contract route: {@link CONTRACT_ROUTE_KERNEL_CLASS} at
* {@link routeKey}`(entry)`.
*
* @param entry a route entry of either fold, in OpenAPI `{param}` form.
* @returns the frozen class.
* @throws `Error('kernelClassOf: no kernel class for <METHOD> <path>')` when the map holds no class
*   for the key. It never answers `plain` for an unknown route (index defaults row 3, refuse over
*   accept), so a new route without a class fails the WS-19 census spec. The lookup reads own
*   keys of the map only (`Object.hasOwn`). Pure.
*/
function kernelClassOf(entry) {
	const key = routeKey(entry);
	if (!Object.hasOwn(CONTRACT_ROUTE_KERNEL_CLASS, key)) throw new Error(`kernelClassOf: no kernel class for ${key}`);
	return CONTRACT_ROUTE_KERNEL_CLASS[key];
}
/** The longest request path that {@link matchContractRoute} reads, in characters. */
const MATCH_PATH_MAX_LENGTH = 2048;
const PARAM_SEGMENT_PATTERN = /^\{([A-Za-z]+)\}$/;
function compileEntry(entry) {
	return {
		entry,
		segments: entry.path.slice(1).split("/").map((segment) => {
			const param = PARAM_SEGMENT_PATTERN.exec(segment);
			return param === null ? { literal: segment } : { param: param[1] };
		})
	};
}
function literalCount(compiled) {
	return compiled.segments.filter((segment) => "literal" in segment).length;
}
/**
* Both folds, compiled once at module load and ordered by literal segment count, the most first.
* The sort is stable, so entries with equal counts keep the fold order. The first entry that
* matches is so the entry with the most literal segments.
*/
const COMPILED_ENTRIES = [...ADMIN_CONTRACT_ROUTES, ...KERNEL_CONTRACT_ROUTES].map(compileEntry).sort((a, b) => literalCount(b) - literalCount(a));
/**
* Map a request-form path to its census entry in either fold.
*
* Inputs: an HTTP method, and a path of 1 to 2,048 characters that starts with `/v1/` and holds no
* `?` or `#`. Each segment must be non-empty, of the unreserved characters `[A-Za-z0-9._~-]` and
* percent triplets, and other than `.` and `..` in any spelling (`%2e` included). A path outside
* that grammar answers `null`.
*
* Matching: a literal segment matches only itself, byte for byte. A `{param}` matches one valid
* segment. When two entries match, the one with more literal segments wins, as
* `GET /v1/admin/apps-catalog/retained` wins over `GET /v1/admin/apps-catalog/{appId}`.
*
* Output: the entry and each raw segment, still percent-encoded, or `null` when no entry of the
* method matches. It never throws. Who calls it: the step-up create route (WS-12a), which then
* reads {@link kernelClassOf} of the entry, and the WS-19 guard. Pure.
*
* @param method the request method.
* @param requestPath the request path, with no query and no fragment.
*/
function matchContractRoute(method, requestPath) {
	if (requestPath.length > MATCH_PATH_MAX_LENGTH || !isRequestPath(requestPath)) return null;
	const segments = requestPath.slice(1).split("/");
	for (const compiled of COMPILED_ENTRIES) {
		if (compiled.entry.method !== method || compiled.segments.length !== segments.length) continue;
		const params = {};
		if (compiled.segments.every((segment, index) => {
			const value = segments[index];
			if ("literal" in segment) return segment.literal === value;
			params[segment.param] = value;
			return true;
		})) return Object.freeze({
			entry: compiled.entry,
			params: Object.freeze(params)
		});
	}
	return null;
}
//#endregion
export { ADMIN_CONTRACT_ROUTES, ADMIN_CONTRACT_ROUTE_FAMILIES, CONTRACT_ROUTE_KERNEL_CLASS, KERNEL_CONTRACT_ROUTES, KERNEL_CONTRACT_ROUTE_FAMILIES, kernelClassOf, kernelRouteClassSchema, matchContractRoute, routeKey };

//# sourceMappingURL=route-census.js.map